import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SITE } from "@/config/site";

const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-heading font-bold text-charcoal text-lg">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] text-zinc-600 leading-relaxed">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-offwhite" data-testid="privacy-policy-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-4">Legal</p>
        <h1 className="font-heading font-extrabold tracking-tight text-charcoal text-3xl sm:text-4xl" data-testid="privacy-heading">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-zinc-500">Westline Technologies — Western Australia, Australia. Last updated: July 2026.</p>

        <Section title="Overview">
          <p>
            This Privacy Policy explains, in plain language, how Westline Technologies (&ldquo;we&rdquo;, &ldquo;us&rdquo;)
            handles personal information collected through this website. We handle personal information in
            accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles, to the extent they
            apply to our business.
          </p>
        </Section>

        <Section title="What information we may collect">
          <p>When you submit an enquiry through this website, we may collect:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>your name and business name;</li>
            <li>your email address and phone number;</li>
            <li>your industry, approximate number of employees and website address; and</li>
            <li>the information you choose to provide about your current systems, processes and what you would like to improve.</li>
          </ul>
          <p>
            Like most websites, basic technical information (such as browser type and pages visited) may be
            collected automatically by the systems that host and operate this website.
          </p>
        </Section>

        <Section title="How we use this information">
          <p>Information submitted through the enquiry form is used to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>respond to your enquiry and communicate with you about it;</li>
            <li>understand your business operations so we can discuss whether our services are a suitable fit; and</li>
            <li>prepare for any discovery call or proposal you request.</li>
          </ul>
          <p>We do not sell your personal information, and we do not use enquiry information for unrelated marketing lists.</p>
        </Section>

        <Section title="How information is stored and secured">
          <p>
            Enquiry submissions are stored in access-controlled systems and are not publicly accessible. We take
            reasonable steps to protect personal information from misuse, loss and unauthorised access. However,
            no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="Third-party technology providers">
          <p>
            This website is operated using third-party hosting, database and email delivery providers. Information
            you submit may be processed or stored by these providers on our behalf, subject to their own security
            practices and terms. Where we later integrate other software (for example, email or analytics
            services), those providers may also process limited information as part of delivering their service.
          </p>
        </Section>

        <Section title="Website analytics">
          <p>
            We may enable website analytics in future to understand how visitors use the site (for example, which
            pages are visited). If enabled, analytics providers may collect technical and usage information in
            accordance with their own privacy policies. This policy will be updated if analytics are introduced.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            This website may use cookies or similar technologies where required for the site to function (for
            example, to keep an administrative session active) or, in future, for analytics. You can control or
            delete cookies through your browser settings.
          </p>
        </Section>

        <Section title="Access, correction and questions">
          <p>
            You may contact us at any time to request access to the personal information we hold about you, to ask
            for it to be corrected, or to ask questions about how it is handled.
          </p>
          <p>
            Westline Technologies — {SITE.location}<br />
            Email: <a href={`mailto:${SITE.email}`} className="text-forest hover:underline" data-testid="privacy-email-link">{SITE.email}</a>
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. The current version will always be available on
            this page.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
